import {
  Button,
  cx,
  DialogTrigger,
  LoadingIndicator,
} from "@light-technology/components";
import { ChevronDown, ChevronUp, Plus } from "@untitledui/icons";
import { useState } from "react";
import { api } from "../../state/api";
import {
  DeviceType,
  ProgramType,
  type AppAccount,
  type Device,
  type AppResponse,
} from "../../state/api.types";
import {
  getIsMissing,
  getNeedsAttention,
  getProgramDevices,
  getServingDevices,
  programTypeToDeviceTypes,
} from "../../utilities/devices";
import { DetailItem } from "../shared/DetailItem";
import { ManufacturerIcon } from "../shared/ManufacturerIcon";
import { getManufacturerDisplayName } from "../shared/manufacturers";
import { AddDeviceModal } from "./AddDeviceModal";
import { ComplianceChecklist } from "./ComplianceChecklist";
import { DeviceIcon } from "./DeviceStatuses";

type AccountSlideoutDevicesProps = {
  app: AppResponse;
  account: AppAccount;
};

export function AccountSlideoutDevices(props: AccountSlideoutDevicesProps) {
  const getDevices = api.useGetAccountDevicesQuery({
    accountUUID: props.account.uuid,
    appUUID: props.app.uuid,
  });

  const getPrograms = api.useGetAccountProgramAgreementsQuery({
    accountUUID: props.account.uuid,
    appUUID: props.app.uuid,
  });

  const devices = getDevices.data?.data ?? [];

  const programs = getPrograms.data?.data ?? [];

  if (getDevices.isLoading || getPrograms.isLoading) {
    return <LoadingIndicator type="brand-spinner" />;
  }

  if (programs.length === 0) {
    return (
      <div className="flex flex-col items-center pr-3xl pb-3xl pl-3xl">
        <div className="text-md text-primary">No devices found</div>
        <div className="text-sm text-tertiary">
          Any connected devices will be found here.
        </div>
      </div>
    );
  }

  const devicesByProgram = getProgramDevices({
    devices,
    programs: programs.map((p) => p.program),
  });

  const panes = [...devicesByProgram].flatMap(
    ([program, matched_devices]): AccountDevicePaneProps[] => {
      const program_type = program.program_type;
      const program_devices = getServingDevices(matched_devices);

      const paneBase = {
        app: props.app,
        account: props.account,
        programName: program.name,
        programType: program_type,
        expectedDeviceTypes: programTypeToDeviceTypes[program_type],
      };

      if (program_devices.length === 0) {
        return [
          {
            ...paneBase,
            device: undefined,
            deviceType: PROGRAM_DEVICE_LABELS[program_type],
            manufacturer: undefined,
            model: undefined,
            identifier: undefined,
          },
        ];
      }
      return program_devices.map((device) => ({
        ...paneBase,
        device,
        ...getDeviceDetails(device),
      }));
    },
  );

  return (
    <div className="flex flex-col gap-3xl">
      {panes.map((pane) => (
        <AccountDevicePane
          key={`${pane.programName}-${pane.device?.uuid ?? "missing"}`}
          collapsible={panes.length > 1}
          {...pane}
        />
      ))}
    </div>
  );
}

const PROGRAM_DEVICE_LABELS: Record<ProgramType, string> = {
  [ProgramType.Battery]: "Battery",
  [ProgramType.Evcharging]: "EV charging",
};

function getDeviceDetails(
  device: Device,
): Pick<
  AccountDevicePaneProps,
  "deviceType" | "manufacturer" | "model" | "identifier"
> {
  const [deviceType, details] = {
    [DeviceType.Battery]: ["Battery", device.battery_details] as const,
    [DeviceType.Evse]: ["EV charger", device.charger_details] as const,
    [DeviceType.Vehicle]: ["Electric vehicle", device.vehicle_details] as const,
  }[device.device_type];
  return {
    deviceType,
    manufacturer: details?.manufacturer,
    model: details?.model,
    identifier: details?.identifier,
  };
}

type AccountDevicePaneProps = {
  app: AppResponse;
  account: AppAccount;
  programName: string;
  programType: ProgramType;
  expectedDeviceTypes: readonly DeviceType[];
  device: Device | undefined;
  deviceType: string | undefined;
  manufacturer: string | undefined;
  model: string | undefined;
  identifier: string | undefined;
  /** Starts collapsed to its header, with a toggle to show the details. */
  collapsible?: boolean;
};

export function AccountDevicePane(props: AccountDevicePaneProps) {
  const [isAddDeviceOpen, setIsAddDeviceOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(!props.collapsible);

  const deviceMissing = getIsMissing(props.device);
  const needsAttention = getNeedsAttention(props.device);
  const deviceName = deviceMissing
    ? "Unknown"
    : props.manufacturer
      ? [getManufacturerDisplayName(props.manufacturer), props.model]
          .filter(Boolean)
          .join(" ")
      : props.deviceType;

  const icons = deviceMissing ? (
    <DeviceIcon
      isMissing={true}
      needsAttention={false}
      programType={props.programType}
    />
  ) : (
    <div className="-space-xs flex">
      <DeviceIcon
        isMissing={false}
        needsAttention={needsAttention}
        className="z-10 bg-primary"
        programType={props.programType}
      />
      {props.manufacturer && (
        <ManufacturerIcon
          manufacturer={props.manufacturer}
          className="-ml-xs"
        />
      )}
    </div>
  );

  return (
    <div
      className={cx(
        "flex flex-col gap-3xl rounded-xl border border-primary p-xl",
        !isExpanded && "cursor-pointer",
      )}
      onClick={
        isExpanded
          ? undefined
          : () => {
              setIsExpanded(true);
            }
      }
    >
      {props.collapsible ? (
        <div className="flex items-center gap-lg">
          {icons}
          <div className="min-w-0 flex-auto">
            <div className="truncate font-mono text-xs text-tertiary">
              {props.deviceType}
            </div>
            <div className="truncate text-md font-medium text-secondary">
              {deviceName}
            </div>
          </div>
          <Button
            className="flex-none"
            color="tertiary"
            aria-label={isExpanded ? "Collapse device" : "Expand device"}
            aria-expanded={isExpanded}
            iconLeading={isExpanded ? ChevronUp : ChevronDown}
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
          />
        </div>
      ) : (
        <div className="flex items-center gap-lg">
          {icons}
          <div>
            <div className="text-md text-secondary">{deviceName}</div>
            <div className="text-xs text-tertiary">{props.deviceType}</div>
          </div>
        </div>
      )}
      {isExpanded && (
        <>
          <DetailItem
            label="Device Program"
            value={props.programName}
            actions={[]}
          />
          {deviceMissing ? (
            <>
              <div className="text-sm">
                Customer is enrolled in a device program that requires a{" "}
                {props.deviceType} device, but no device has been connected.
              </div>
              <DialogTrigger
                isOpen={isAddDeviceOpen}
                onOpenChange={setIsAddDeviceOpen}
              >
                <Button
                  className="self-start"
                  size="sm"
                  iconLeading={Plus}
                  onClick={() => {
                    setIsAddDeviceOpen(true);
                  }}
                >
                  Add Device
                </Button>
                <AddDeviceModal
                  account={props.account}
                  app={props.app}
                  onClose={() => {
                    setIsAddDeviceOpen(false);
                  }}
                  deviceTypes={props.expectedDeviceTypes}
                />
              </DialogTrigger>
            </>
          ) : props.device ? (
            <>
              <ComplianceChecklist
                app={props.app}
                device={props.device}
                account={props.account}
              />
              <DetailItem
                label="UUID"
                value={props.device.uuid}
                actions={["copy"]}
              />
              <DetailItem
                label="Identifier"
                value={props.identifier}
                actions={["copy"]}
              />
              {props.programType === ProgramType.Battery ? (
                <DetailItem
                  label="Capacity"
                  value={
                    props.device.battery_details?.capacity_kwh
                      ? `${props.device.battery_details.capacity_kwh} kWh`
                      : undefined
                  }
                  actions={[]}
                />
              ) : null}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
